import React, { useCallback, useEffect, useState } from 'react';
import { Tag, Plus, History, UserPlus, RefreshCw, Pencil, Trash2, Dices, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import { promoCodesAPI, AdminPromoCode } from '../services/api';

const PROMO_CODE_CHARS = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

function generateRandomPromoCode(length = 10): string {
  const buf = new Uint32Array(length);
  crypto.getRandomValues(buf);
  let s = '';
  for (let i = 0; i < length; i++) {
    s += PROMO_CODE_CHARS[buf[i]! % PROMO_CODE_CHARS.length];
  }
  return s;
}

function toDatetimeLocal(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

type FormState = {
  code: string;
  subscriptionType: 'standard' | 'premium';
  discountKind: 'percent' | 'fixed_kopecks';
  discountValue: number;
  maxUses: string;
  validFrom: string;
  validUntil: string;
  restrictedUserId: string;
  allowsMultiplePayments: boolean;
};

const emptyForm = (): FormState => ({
  code: '',
  subscriptionType: 'standard',
  discountKind: 'percent',
  discountValue: 10,
  maxUses: '',
  validFrom: '',
  validUntil: '',
  restrictedUserId: '',
  allowsMultiplePayments: false,
});

type EditFormState = FormState & { isActive: boolean };

const emptyEditForm = (): EditFormState => ({ ...emptyForm(), isActive: true });

const PromoCodesPage: React.FC = () => {
  const [items, setItems] = useState<AdminPromoCode[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [eventsFor, setEventsFor] = useState<number | null>(null);
  const [events, setEvents] = useState<Array<Record<string, unknown>>>([]);
  const [assignmentsFor, setAssignmentsFor] = useState<number | null>(null);
  const [assignmentRows, setAssignmentRows] = useState<
    Array<{
      id: number;
      userId: number;
      status: string;
      assignedAt: string;
      assignedByAdminId?: number | null;
      consumedAt?: string | null;
    }>
  >([]);
  const [assignFor, setAssignFor] = useState<number | null>(null);
  const [assignUserId, setAssignUserId] = useState('');

  const [form, setForm] = useState<FormState>(emptyForm);

  const [editId, setEditId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<EditFormState>(emptyEditForm);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await promoCodesAPI.list(100, 0);
      if (r.success) {
        setItems(r.items || []);
        setTotal(r.total ?? 0);
      } else {
        toast.error('Не удалось загрузить промокоды');
      }
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'response' in e
          ? (e as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      toast.error(msg || 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openEvents = async (id: number) => {
    setEventsFor(id);
    try {
      const r = await promoCodesAPI.events(id);
      if (r.success && r.events) {
        setEvents(r.events as unknown as Array<Record<string, unknown>>);
      } else {
        setEvents([]);
      }
    } catch {
      toast.error('Не удалось загрузить события');
      setEvents([]);
    }
  };

  const openAssignments = async (id: number) => {
    setAssignmentsFor(id);
    try {
      const r = await promoCodesAPI.assignments(id);
      if (r.success && r.items) {
        setAssignmentRows(r.items);
      } else {
        setAssignmentRows([]);
      }
    } catch {
      toast.error('Не удалось загрузить назначения');
      setAssignmentRows([]);
    }
  };

  const assignmentStatusRu = (s: string) => {
    switch (s) {
      case 'active':
        return 'активно';
      case 'consumed':
        return 'оплачено';
      case 'expired':
        return 'истекло';
      case 'revoked':
        return 'отозвано';
      default:
        return s;
    }
  };

  const confirmRevokeAssignment = async (
    promoId: number,
    row: { id: number; userId: number; status: string }
  ) => {
    if (row.status !== 'active') {
      toast.error('Отозвать можно только активное назначение');
      return;
    }
    if (!window.confirm(`Отозвать промокод у пользователя ${row.userId}? Он сможет применить код снова.`)) return;
    try {
      const r = await promoCodesAPI.revokeAssignment(promoId, row.id);
      if (r.success) {
        toast.success('Назначение отозвано');
        await openAssignments(promoId);
        load();
      } else {
        toast.error(r.message || 'Ошибка');
      }
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      toast.error(msg || 'Ошибка отзыва');
    }
  };

  const openEdit = async (id: number) => {
    try {
      const r = await promoCodesAPI.get(id);
      if (!r.success || !r.promo) {
        toast.error('Не удалось загрузить промокод');
        return;
      }
      const p = r.promo;
      setEditForm({
        code: p.code,
        subscriptionType: (p.subscriptionType === 'premium' ? 'premium' : 'standard') as 'standard' | 'premium',
        discountKind: (p.discountKind === 'fixed_kopecks' ? 'fixed_kopecks' : 'percent') as 'percent' | 'fixed_kopecks',
        discountValue: p.discountValue,
        maxUses: p.maxUses != null ? String(p.maxUses) : '',
        validFrom: p.validFrom ? toDatetimeLocal(p.validFrom) : '',
        validUntil: p.validUntil ? toDatetimeLocal(p.validUntil) : '',
        restrictedUserId: p.restrictedUserId != null ? String(p.restrictedUserId) : '',
        allowsMultiplePayments: p.allowsMultiplePayments,
        isActive: p.isActive,
      });
      setEditId(id);
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      toast.error(msg || 'Ошибка загрузки');
    }
  };

  const submitCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const body: Record<string, unknown> = {
      code: form.code.trim(),
      subscriptionType: form.subscriptionType,
      discountKind: form.discountKind,
      discountValue: form.discountValue,
      allowsMultiplePayments: form.allowsMultiplePayments,
    };
    if (form.maxUses !== '') body.maxUses = parseInt(form.maxUses, 10);
    if (form.validFrom) body.validFrom = new Date(form.validFrom).toISOString();
    if (form.validUntil) body.validUntil = new Date(form.validUntil).toISOString();
    if (form.restrictedUserId) body.restrictedUserId = parseInt(form.restrictedUserId, 10);
    try {
      const r = await promoCodesAPI.create(body);
      if (r.success) {
        toast.success('Промокод создан');
        setShowCreate(false);
        setForm(emptyForm());
        load();
      } else {
        toast.error(r.message || 'Ошибка');
      }
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      toast.error(msg || 'Ошибка создания');
    }
  };

  const submitEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editId == null) return;
    const body: Record<string, unknown> = {
      code: editForm.code.trim(),
      subscriptionType: editForm.subscriptionType,
      discountKind: editForm.discountKind,
      discountValue: editForm.discountValue,
      allowsMultiplePayments: editForm.allowsMultiplePayments,
      isActive: editForm.isActive,
    };
    if (editForm.maxUses === '') body.setMaxUsesToNull = true;
    else body.maxUses = parseInt(editForm.maxUses, 10);
    body.validFrom = editForm.validFrom ? new Date(editForm.validFrom).toISOString() : '';
    body.validUntil = editForm.validUntil ? new Date(editForm.validUntil).toISOString() : '';
    body.restrictedUserId = editForm.restrictedUserId.trim();
    try {
      const r = await promoCodesAPI.patch(editId, body);
      if (r.success) {
        toast.success('Промокод сохранён');
        setEditId(null);
        setEditForm(emptyEditForm());
        load();
      } else {
        toast.error(r.message || 'Ошибка');
      }
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      toast.error(msg || 'Ошибка сохранения');
    }
  };

  const confirmDelete = async (p: AdminPromoCode) => {
    if (!window.confirm(`Удалить промокод «${p.code}»? Назначения пользователям будут удалены.`)) return;
    try {
      const r = await promoCodesAPI.delete(p.id);
      if (r.success) {
        toast.success('Удалено');
        load();
      } else {
        toast.error(r.message || 'Ошибка');
      }
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      toast.error(msg || 'Ошибка удаления');
    }
  };

  const submitAssign = async () => {
    if (!assignFor || !assignUserId.trim()) return;
    try {
      const uid = parseInt(assignUserId, 10);
      const r = await promoCodesAPI.assign(assignFor, uid);
      if (r.success) {
        toast.success('Назначено пользователю');
        setAssignFor(null);
        setAssignUserId('');
        if (eventsFor === assignFor) openEvents(assignFor);
        load();
      } else {
        toast.error(r.message || 'Ошибка');
      }
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      toast.error(msg || 'Ошибка назначения');
    }
  };

  const fmtDisc = (p: AdminPromoCode) =>
    p.discountKind === 'percent' ? `−${p.discountValue}%` : `−${(p.discountValue / 100).toFixed(0)} ₽`;

  function renderPromoFields<T extends FormState>(
    f: T,
    setF: React.Dispatch<React.SetStateAction<T>>,
    opts: { showRandom: boolean; id?: string }
  ) {
    return (
    <>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Код</label>
        <div className="flex gap-2">
          <input
            id={opts.id ? `${opts.id}-code` : undefined}
            className="flex-1 border rounded-lg px-3 py-2 font-mono"
            value={f.code}
            onChange={(e) => setF({ ...f, code: e.target.value })}
            required
            placeholder="SUMMER2025"
          />
          {opts.showRandom && (
            <button
              type="button"
              className="inline-flex items-center gap-1 shrink-0 px-3 py-2 border border-indigo-200 rounded-lg text-indigo-700 hover:bg-indigo-50 text-sm"
              onClick={() => setF({ ...f, code: generateRandomPromoCode(10) })}
              title="Сгенерировать случайный код"
            >
              <Dices className="h-4 w-4" />
              Случайный
            </button>
          )}
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Тариф</label>
        <select
          className="w-full border rounded-lg px-3 py-2"
          value={f.subscriptionType}
          onChange={(e) => setF({ ...f, subscriptionType: e.target.value as 'standard' | 'premium' })}
        >
          <option value="standard">Стандарт</option>
          <option value="premium">Про</option>
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Тип скидки</label>
        <select
          className="w-full border rounded-lg px-3 py-2"
          value={f.discountKind}
          onChange={(e) => setF({ ...f, discountKind: e.target.value as 'percent' | 'fixed_kopecks' })}
        >
          <option value="percent">Процент</option>
          <option value="fixed_kopecks">Фикс (копейки)</option>
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Значение</label>
        <input
          type="number"
          min={1}
          className="w-full border rounded-lg px-3 py-2"
          value={f.discountValue}
          onChange={(e) => setF({ ...f, discountValue: parseInt(e.target.value, 10) || 0 })}
        />
        <p className="text-xs text-gray-500 mt-1">Для процента — 1–100; для фикса — сумма в копейках</p>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Макс. использований (пусто = без лимита)</label>
        <input
          className="w-full border rounded-lg px-3 py-2"
          value={f.maxUses}
          onChange={(e) => setF({ ...f, maxUses: e.target.value })}
          placeholder=""
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Только для user ID (необязательно)</label>
        <input
          className="w-full border rounded-lg px-3 py-2"
          value={f.restrictedUserId}
          onChange={(e) => setF({ ...f, restrictedUserId: e.target.value })}
          placeholder=""
        />
        <p className="text-xs text-gray-500 mt-1">При редактировании: пустое поле снимает ограничение</p>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Действует с</label>
        <input
          type="datetime-local"
          className="w-full border rounded-lg px-3 py-2"
          value={f.validFrom}
          onChange={(e) => setF({ ...f, validFrom: e.target.value })}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Действует до</label>
        <input
          type="datetime-local"
          className="w-full border rounded-lg px-3 py-2"
          value={f.validUntil}
          onChange={(e) => setF({ ...f, validUntil: e.target.value })}
        />
      </div>
      <div className="md:col-span-2 flex items-center gap-2">
        <input
          type="checkbox"
          id={opts.id ? `${opts.id}-multi` : 'multi'}
          checked={f.allowsMultiplePayments}
          onChange={(e) => setF({ ...f, allowsMultiplePayments: e.target.checked })}
        />
        <label htmlFor={opts.id ? `${opts.id}-multi` : 'multi'} className="text-sm text-gray-700">
          Многоразовый для одного пользователя (не снимать после первой оплаты)
        </label>
      </div>
    </>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Tag className="h-8 w-8 text-indigo-600" />
            Промокоды
          </h1>
          <p className="text-gray-600 mt-1">Создание кодов, назначение пользователям, история событий</p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => load()}
            className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <RefreshCw className="h-4 w-4" />
            Обновить
          </button>
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            <Plus className="h-4 w-4" />
            Новый промокод
          </button>
        </div>
      </div>

      {showCreate && (
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <h2 className="text-lg font-semibold mb-4">Новый промокод</h2>
          <form onSubmit={submitCreate} className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl">
            {renderPromoFields(form, setForm, { showRandom: true, id: 'create' })}
            <div className="md:col-span-2 flex gap-2">
              <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
                Создать
              </button>
              <button type="button" className="px-4 py-2 border rounded-lg" onClick={() => setShowCreate(false)}>
                Отмена
              </button>
            </div>
          </form>
        </div>
      )}

      {editId != null && (
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <h2 className="text-lg font-semibold mb-4">Редактирование #{editId}</h2>
          <form onSubmit={submitEdit} className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl">
            {renderPromoFields(editForm, setEditForm, {
              showRandom: true,
              id: 'edit',
            })}
            <div className="md:col-span-2 flex items-center gap-2">
              <input
                type="checkbox"
                id="edit-active"
                checked={editForm.isActive}
                onChange={(e) => setEditForm({ ...editForm, isActive: e.target.checked })}
              />
              <label htmlFor="edit-active" className="text-sm text-gray-700">
                Промокод активен
              </label>
            </div>
            <div className="md:col-span-2 flex gap-2">
              <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
                Сохранить
              </button>
              <button
                type="button"
                className="px-4 py-2 border rounded-lg"
                onClick={() => {
                  setEditId(null);
                  setEditForm(emptyEditForm());
                }}
              >
                Отмена
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 text-sm text-gray-600">Всего: {total}</div>
        {loading ? (
          <div className="p-8 text-center text-gray-500">Загрузка…</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Код</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Тариф</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Скидка</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Применено</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Использовано</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">До</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Активен</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Действия</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {items.map((p) => (
                  <tr key={p.id}>
                    <td className="px-4 py-2 font-mono font-medium">{p.code}</td>
                    <td className="px-4 py-2">{p.subscriptionType === 'premium' ? 'Про' : 'Стандарт'}</td>
                    <td className="px-4 py-2">{fmtDisc(p)}</td>
                    <td className="px-4 py-2">{p.appliedCount ?? 0}</td>
                    <td className="px-4 py-2">
                      {p.usesCount}
                      {p.maxUses != null ? ` / ${p.maxUses}` : ''}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-600">
                      {p.validUntil ? new Date(p.validUntil).toLocaleString('ru-RU') : '—'}
                    </td>
                    <td className="px-4 py-2">{p.isActive ? 'да' : 'нет'}</td>
                    <td className="px-4 py-2 text-right space-x-2 whitespace-nowrap">
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 text-amber-700 text-sm hover:underline"
                        onClick={() => openEdit(p.id)}
                      >
                        <Pencil className="h-4 w-4" />
                        Изменить
                      </button>
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 text-indigo-600 text-sm hover:underline"
                        onClick={() => openEvents(p.id)}
                      >
                        <History className="h-4 w-4" />
                        События
                      </button>
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 text-sky-700 text-sm hover:underline"
                        onClick={() => openAssignments(p.id)}
                      >
                        <Users className="h-4 w-4" />
                        Кто применил
                      </button>
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 text-green-700 text-sm hover:underline"
                        onClick={() => {
                          setAssignFor(p.id);
                          setAssignUserId('');
                        }}
                      >
                        <UserPlus className="h-4 w-4" />
                        Назначить
                      </button>
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 text-red-600 text-sm hover:underline"
                        onClick={() => confirmDelete(p)}
                      >
                        <Trash2 className="h-4 w-4" />
                        Удалить
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {items.length === 0 && <div className="p-8 text-center text-gray-500">Промокодов пока нет</div>}
          </div>
        )}
      </div>

      {assignmentsFor != null && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
          onClick={() => setAssignmentsFor(null)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[80vh] overflow-auto p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold mb-4">Назначения промокода #{assignmentsFor}</h3>
            <p className="text-sm text-gray-600 mb-4">
              Активное назначение можно отозвать — пользователь снова сможет ввести этот промокод.
            </p>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">User ID</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Статус</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Назначено</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Админ</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Действия</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {assignmentRows.map((row) => (
                    <tr key={row.id}>
                      <td className="px-3 py-2 font-mono">{row.userId}</td>
                      <td className="px-3 py-2">{assignmentStatusRu(row.status)}</td>
                      <td className="px-3 py-2 text-gray-600">
                        {row.assignedAt ? new Date(row.assignedAt).toLocaleString('ru-RU') : '—'}
                      </td>
                      <td className="px-3 py-2 text-gray-600">
                        {row.assignedByAdminId != null ? String(row.assignedByAdminId) : '—'}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {row.status === 'active' ? (
                          <button
                            type="button"
                            className="text-amber-700 hover:underline"
                            onClick={() => confirmRevokeAssignment(assignmentsFor, row)}
                          >
                            Отозвать
                          </button>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {assignmentRows.length === 0 && <p className="text-gray-500 mt-2">Назначений пока нет</p>}
            <button type="button" className="mt-4 px-4 py-2 border rounded-lg" onClick={() => setAssignmentsFor(null)}>
              Закрыть
            </button>
          </div>
        </div>
      )}

      {eventsFor != null && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setEventsFor(null)}>
          <div
            className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-auto p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold mb-4">События (промокод #{eventsFor})</h3>
            <ul className="space-y-2 text-sm">
              {events.map((ev) => (
                <li key={String(ev.id)} className="border-b border-gray-100 pb-2">
                  <div className="font-medium">{String(ev.eventType)}</div>
                  <div className="text-gray-600 text-xs">
                    {ev.createdAt ? new Date(String(ev.createdAt)).toLocaleString('ru-RU') : ''}
                    {ev.userId != null ? ` · user ${String(ev.userId)}` : ''}
                    {ev.adminId != null ? ` · admin ${String(ev.adminId)}` : ''}
                    {ev.paymentId != null ? ` · payment ${String(ev.paymentId)}` : ''}
                  </div>
                </li>
              ))}
            </ul>
            {events.length === 0 && <p className="text-gray-500">Событий нет</p>}
            <button type="button" className="mt-4 px-4 py-2 border rounded-lg" onClick={() => setEventsFor(null)}>
              Закрыть
            </button>
          </div>
        </div>
      )}

      {assignFor != null && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setAssignFor(null)}>
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-4">Назначить пользователю (ID)</h3>
            <input
              type="number"
              className="w-full border rounded-lg px-3 py-2 mb-4"
              placeholder="User ID"
              value={assignUserId}
              onChange={(e) => setAssignUserId(e.target.value)}
            />
            <div className="flex gap-2">
              <button type="button" className="px-4 py-2 bg-indigo-600 text-white rounded-lg" onClick={submitAssign}>
                Назначить
              </button>
              <button type="button" className="px-4 py-2 border rounded-lg" onClick={() => setAssignFor(null)}>
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromoCodesPage;
